import LoadingIcon from "@/components/layout/loading-icon";
import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "antd";
import classNames from "classnames";
import { FC, useEffect } from "react";
import { BiCheck } from "react-icons/bi";
import styles from "./card.module.scss";
import StepperInput from "@/features/bridge/components/inputs/stepper-input";
import CardImageLoader from "@/features/bridge/components/token-selector/selector-interface/card-image-loader";
import { fetchNft } from "@/features/bridge/services/alchemy/getNft";
import { useMultiTokenManager } from "@/features/bridge/stores/multi-token-manager";
import type { ModifiedNftToken } from "@/features/bridge/types/open-sea-api.types";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import { useMainnetToTestnetSwitchStore } from "@/stores/mainet-testnet-switcher.store";
import { BLESSNET_CHAIN_ID } from "@/features/bridge/constants/bridge.constant";
import { useTokenEligibility, getEligibilityMessage } from "@/features/bridge/hooks/useTokenEligibility.hook";

type Props = {
  token: ModifiedNftToken;
  collection: string;
};

const MultiCard: FC<Props> = ({ token }) => {
  const {
    bridgeControls: {
      fromChain: { originChain },
      account,
    },
  } = useBridge();
  const {
    toggleToken,
    changeTokenAmount,
    tokenIncrement,
    tokenDecrement,
    setExistingTokensCount,
  } = useMultiTokenManager();

  // Check token eligibility for bridging
  const eligibility = useTokenEligibility(token.address);
  const eligibilityMessage = getEligibilityMessage(eligibility);

  //$ Gets the specific data pertaining to this token
  // Skip OpenSea API calls for Blessnet tokens to avoid 404 errors
  const isBlessnetToken = originChain?.chainId === BLESSNET_CHAIN_ID;
  const { data, isLoading } = useQuery({
    queryKey: [TokenType.MULTI, token.identifier],
    queryFn: () =>
      fetchNft(token.address, token.identifier, originChain?.chainId),
    enabled: !!token.identifier && !!originChain?.chainId && !!token.address && !isBlessnetToken,
  });

  //$ Sets the amount available on this token into state
  useEffect(() => {
    if (isBlessnetToken) {
      // For Blessnet tokens, use the availableTokens from the initial API response
      if (token.availableTokens) {
        setExistingTokensCount(
          token.identifier,
          token.collection,
          token.availableTokens,
        );
      }
    } else if (data) {
      // For non-Blessnet tokens, use OpenSea data
      const owner = data.nft.owners.find(
        (owner) =>
          owner.address.toLowerCase() === account.address?.toLowerCase(),
      );
      if (owner?.quantity)
        setExistingTokensCount(
          token.identifier,
          token.collection,
          owner.quantity,
        );
    }
  }, [
    account.address,
    data,
    isBlessnetToken,
    setExistingTokensCount,
    token.availableTokens,
    token.collection,
    token.identifier,
  ]);

  //* Handles clicks of the token card (image/text area only)
  const handleClick = () => {
    // Don't allow interaction if token is not eligible
    if (!eligibility.isEligible) return;
    toggleToken(token);
  };

  //* Handles clicks on the input field - prevent toggle and let input handle focus
  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the card click handler from firing

    // Don't allow interaction if token is not eligible
    if (!eligibility.isEligible) return;

    // If token is not selected yet, select it first
    if (token.amountToBridge === 0) {
      toggleToken(token);
    }
    // StepperInput will handle text selection automatically via its own onClick
  };

  //* Handles setting max amount
  const handleMaxAmount = () => {
    if (token.availableTokens) changeTokenAmount(token, token.availableTokens);
  };

  return (
    <Tooltip
      title={!eligibility.isEligible ? eligibilityMessage : undefined}
      placement={'center' as any}
    >
      <button
        disabled={!isBlessnetToken && !data || !eligibility.isEligible}
        className={classNames(
          styles.cardContainer,
          (token.amountToBridge && (token.amountToBridge > 0 || token.amountToBridge === -1)) && styles.active,
          !eligibility.isEligible && styles.disabled,
        )}
        onClick={handleClick}
      >
      <div className={styles.image}>
        <CardImageLoader
          src={token.display_image_url}
          alt={token.name + " display image"}
          identifier={token.identifier}
          symbol={token.symbol}
          contractAddress={token.address}
          chainId={originChain?.chainId}
          tokenType={token.token_standard}
        />
        <div
          className={classNames(
            styles.tick,
            (token.amountToBridge && (token.amountToBridge > 0 || token.amountToBridge === -1)) && styles.active,
          )}
        >
          <BiCheck size={25} />
        </div>
      </div>
      <div className={styles.text}>
        <p className={styles.cardTitle}>
            {token.name ? token.name : token.identifier}
        </p>
        {isLoading ? (
          <LoadingIcon size={25} />
        ) : (
          <div className={styles.amountWrapper}>
            <p className={styles.amount}>{token.availableTokens} available</p>
          </div>
        )}
      </div>
      {/* TODO Add a existing amount to the input styling , input max should be the currently owned*/}
      <StepperInput
        disabled={isLoading || !eligibility.isEligible}
        fullDisabled={isLoading || !eligibility.isEligible}
        decrementToken={() => tokenIncrement(token)}
        incrementToken={() => tokenDecrement(token)}
        changeTokenAmount={(amount) => changeTokenAmount(token, amount)}
        isLoading={isLoading || eligibility.isLoading}
        value={token.amountToBridge && token.amountToBridge > 0 ? token.amountToBridge : ""}
        handleMaxAmount={handleMaxAmount}
        maxValue={token.availableTokens}
        isFocused={token.amountToBridge === -1 && eligibility.isEligible}
        onInputClick={handleInputClick}
      />
    </button>
    </Tooltip>
  );
};

export default MultiCard;
